/** @jsx jsx */
import * as React from "react";
import { ModalComponent } from "../ModalComponent";
import * as moment from "moment";
import { isEmpty as _isEmpty } from "lodash";
import { AppConstants, CALENDAR_DAYS, DEFAULT_WORKING_HOURS_ACTIONS } from "../../utils/AppConstants";
import { ITimeInterval } from "../../models/Roster/ITimeInterval";
import IDoctorProfile from "../../models/IDoctorProfile";
import AppUtils from "../../utils/AppUtils";
import { Tracker } from "../../utils/Tracker";
import { EventNames } from "../../utils/EventTrackerConstants";
import { RosterTimeSelector } from "./RosterTimeSelector";
import LocalStorageUtils from "../../utils/LocalStorageUtils";
import { rosterStyles } from "../../styles/roster/mobile/roster";
import { css, jsx } from "@emotion/core";
import { default as CTA, CTATheme } from "../common/CallToAction";
import { DefaultWorkingConfirmationModal } from "../defaultWorkingHours/ConfirmationModal";
import { IUpdateDefaultWorkingHoursRequest } from "../../models/defaultWorkingHours/IDefaultWorkingHoursRequest";
import { rosterButtonStyles } from "../../styles/components/Roster/RosterCalendar";
import { ErrorInline } from "../../components/common/ErrorInline";

interface IAddRosterModalProps {
    toggleForceCloseModal(): void;
    title: string;
    date: string;
    addRosterShifts?(shifts: ITimeInterval[], date: string): void;
    selectedDoctor: IDoctorProfile;
    isDefaultWorkingHoursAction?: boolean;
    addDefaultWorkingHoursShift?(addRosterParams: IUpdateDefaultWorkingHoursRequest[], userAction?: DEFAULT_WORKING_HOURS_ACTIONS): void;
}

interface IAddRosterModalState {
    enteredShifts: ITimeInterval[];
    errMsg: string;
    showUserInputModal: boolean;
    showConfirmationModal: boolean;
    effectiveDate: string;
}
export class AddRosterModal extends React.Component<IAddRosterModalProps, IAddRosterModalState> {
    constructor(props: IAddRosterModalProps) {
        super(props);
        this.state = {
            enteredShifts: [{ startTime: null, endTime: null }],
            errMsg: null,
            showConfirmationModal: false,
            showUserInputModal: false,
            effectiveDate: null
        }
        this.addMoreShift = this.addMoreShift.bind(this);
        this.addRoster = this.addRoster.bind(this);
        this.deleteShift = this.deleteShift.bind(this);
        this.shiftTimeChangeHandler = this.shiftTimeChangeHandler.bind(this);
    }

    getTrackComponentName() {
        const { isDefaultWorkingHoursAction } = this.props;
        return isDefaultWorkingHoursAction ? EventNames.Add_Automated_Working_Hours : EventNames.Working_Hours;
    }

    componentDidMount() {
        this.setState({ showUserInputModal: true });
        Tracker.track(this.getTrackComponentName());
    }

    private preventModalClose(e: React.MouseEvent<HTMLDivElement>): void {
        e.preventDefault();
        e.stopPropagation();
    }
    private addMoreShift(): void {
        LocalStorageUtils.setClickEvents({ field: EventNames.Add_Another, widget: EventNames.Add_Working_Hours, flow: this.getTrackComponentName(), section: this.getTrackComponentName() })
        const { enteredShifts } = this.state;
        enteredShifts.push({ startTime: null, endTime: null });
        this.setState({ enteredShifts: [...enteredShifts] });
    }
    private shiftTimeChangeHandler(index: number, value: string, key: "startTime" | "endTime"): void {
        if (value) {
            const { enteredShifts } = this.state;
            enteredShifts[index][key] = value;
            this.setState({ enteredShifts: [...enteredShifts] });
        }
    }
    private addRoster(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
        LocalStorageUtils.setClickEvents({ field: EventNames.Save, widget: EventNames.Add_Working_Hours, flow: this.getTrackComponentName(), section: this.getTrackComponentName() })
        Tracker.track(EventNames.Add_Working_Hours_Save_Button)
        e.stopPropagation();
        e.preventDefault();
        this.setState({ errMsg: null })
        const { enteredShifts } = this.state;
        const { date, isDefaultWorkingHoursAction } = this.props;
        const dateInEpoch: number = isDefaultWorkingHoursAction ? moment(new Date()).valueOf() : moment(date, AppConstants.dateFormat).unix() * 1000;
        if (!AppUtils.isAllShiftPresent(dateInEpoch, enteredShifts)) {
            this.setState({ errMsg: AppConstants.ROSTER_MESSAGES.NULL_SHIFTS });
        }
        else if (AppUtils.isLateNightShift(enteredShifts)) {
            this.setState({ errMsg: AppConstants.ROSTER_MESSAGES.NON_OPERATIONAL_HOURS });
        }
        else if (AppUtils.isOverLappingShiftsPresent(dateInEpoch, enteredShifts)) {
            this.setState({ errMsg: AppConstants.ROSTER_MESSAGES.CONFLICTINF_SHIFTS_IN_DAY });
        }
        else if (AppUtils.isRosterValid(dateInEpoch, enteredShifts)) {
            this.setState({ errMsg: AppUtils.isRosterValid(dateInEpoch, enteredShifts) });
        }
        else {
            if (isDefaultWorkingHoursAction) {
                const effectiveDate = AppUtils.getDateFromNumberOfDays();
                this.setState({ showConfirmationModal: true, showUserInputModal: false, effectiveDate });
            }
            else {
                this.props.addRosterShifts(enteredShifts, date);
            }
        }
    }

    private deleteShift(index: number) {
        const { enteredShifts } = this.state;
        enteredShifts.splice(index, 1);
        this.setState({ enteredShifts });

    }

    handleCancelClick = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Cancel, widget: EventNames.Add_Working_Hours, flow: this.getTrackComponentName(), section: this.getTrackComponentName() })
        Tracker.track(EventNames.Add_Working_Hours_Cancel_button)
        this.props.toggleForceCloseModal()
    }

    editExistingShifts = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Go_Back_To_Change_Selection, widget: EventNames.Automated_Working_Hours_Confirmation_Modal, flow: this.getTrackComponentName(), section: EventNames.Automated_Working_Hours_Confirmation_Modal });
        Tracker.track(EventNames.Go_Back_To_Change_Selection);
        this.setState({ showConfirmationModal: false, showUserInputModal: true });
    }

    saveShifts = () => {
        LocalStorageUtils.setClickEvents({ field: EventNames.Proceed_with_Selection, widget: EventNames.Automated_Working_Hours_Confirmation_Modal, flow: this.getTrackComponentName(), section: EventNames.Automated_Working_Hours_Confirmation_Modal });
        Tracker.track(EventNames.Proceed_with_Selection);
        const { enteredShifts } = this.state;
        const { date, selectedDoctor } = this.props;
        const addParams: IUpdateDefaultWorkingHoursRequest[] = enteredShifts.map(({ startTime, endTime }) => {
            return {
                agentShiftDeallocationMapping: [],
                startTime,
                endTime,
                agentId: selectedDoctor.id,
                centerId: selectedDoctor.agentCenterMapping && selectedDoctor.agentCenterMapping[0] && selectedDoctor.agentCenterMapping[0].centerId,
                dayOfWeek: date,
                shiftName: date,
                isActive: true
            }
        });

        this.props.addDefaultWorkingHoursShift(addParams, DEFAULT_WORKING_HOURS_ACTIONS.ADD);
    }

    render() {
        const { toggleForceCloseModal, title, date, selectedDoctor, isDefaultWorkingHoursAction } = this.props;
        const { enteredShifts, errMsg, showConfirmationModal, showUserInputModal, effectiveDate } = this.state;
        const isMobile = AppUtils.getIsMobile();
        const agentCentreMissing = _isEmpty(selectedDoctor.agentCenterMapping);
        const agentMappedWithMultipleCentres = !agentCentreMissing && selectedDoctor.agentCenterMapping.length > 1;
        return (
            <div className="roster-modal-container">
                {showUserInputModal &&
                    <ModalComponent modalSize={"small"} onClickCloseHandler={toggleForceCloseModal}>
                        <div onClick={this.preventModalClose}>
                            <div className="roster-modal-header">
                                <h3>{title}</h3>
                                <div onClick={toggleForceCloseModal}>x</div>
                            </div>
                            <hr></hr>
                            <div css={isMobile ? rosterStyles.rosterModalBody : {}} className="roster-modal-body">
                                {
                                    isDefaultWorkingHoursAction ? <h3>{Object.keys(CALENDAR_DAYS).find(key => CALENDAR_DAYS[key].value === date)}</h3> : <h3>{moment(date, "DD-MM-YYYY").format("DD MMMM YYYY, dddd")}</h3>
                                }
                                {/* <div className="working-hrs-text">Working Hours</div> */}
                                <table>
                                    {
                                        enteredShifts.map(({ startTime, endTime }, index: number) => {
                                            return (
                                                <React.Fragment key={startTime + endTime + index}>
                                                    <RosterTimeSelector widgetName={isDefaultWorkingHoursAction ? EventNames.Add_Automated_Working_Hours : EventNames.Add_Working_Hours} value={{ startTimeForEdit: startTime, endTimeForEdit: endTime }} date={date} index={index}
                                                        deleteShift={this.deleteShift} shiftTimeChangeHandler={this.shiftTimeChangeHandler} />
                                                </React.Fragment>
                                            )
                                        })
                                    }
                                    <tr className="add-more">
                                        <td colSpan={2}>
                                            <span onClick={this.addMoreShift}>Add another</span>
                                        </td>
                                    </tr>
                                </table>
                                {errMsg &&
                                    <ErrorInline errMsg={errMsg} />
                                }
                                {agentCentreMissing &&
                                    <div className="warning">
                                        <div>* Cannot create your roster.</div>
                                        <div>You are currently not assigned to any center. Please contact Care.Fit team.</div>
                                    </div>
                                }
                                {agentMappedWithMultipleCentres &&
                                    <div className="warning">
                                        <div>* Cannot create your roster.</div>
                                        <div>You are currently assigned to multiple centers. Please contact Care.Fit team.</div>

                                    </div>
                                }
                            </div>
                            <hr></hr>
                            {selectedDoctor &&
                                <div className="roster-modal-footer">
                                    <CTA innerText="Cancel" theme={CTATheme.LIGHT} onClick={this.handleCancelClick} customStyle={rosterButtonStyles} />
                                    <CTA innerText="Save" theme={CTATheme.PRIMARY} onClick={this.addRoster} disabled={selectedDoctor.agentCenterMapping.length !== 1} customStyle={rosterButtonStyles} />
                                </div>
                            }
                        </div>
                    </ModalComponent>
                }
                {
                    showConfirmationModal &&
                    <DefaultWorkingConfirmationModal editShifts={this.editExistingShifts} saveShifts={this.saveShifts} effectiveDate={effectiveDate}></DefaultWorkingConfirmationModal>
                }
            </div >)
    }
}